import axios from 'axios';

import { isUnauthorizedError } from '../services/authErrorHelper';

/**
 * Axios-only: transport failures and retryable HTTP statuses for billing-related fetches.
 * 401/403 and other non-retryable client responses are non-transient; all 5xx are transient.
 * Shared by billing enforcement policy gates and subscription entitlement probing.
 */
export function isTransientBillingRelatedAxiosError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') return true;
  const status = error.response?.status;
  if (status === 401 || status === 403) return false;
  if (error.response == null || status == null) return true;
  if (status === 408 || status === 429) return true;
  if (status >= 500) return true;
  return false;
}

/**
 * Likely network / infra blips — extra retries and softer gate UX are appropriate.
 * Does **not** include 401/403 (handled separately). Uses {@link isTransientBillingRelatedAxiosError}
 * plus non-Axios errno-style network errors.
 */
export function isTransientBillingPolicyFetchError(error: unknown): boolean {
  if (isUnauthorizedError(error)) return false;
  if (isTransientBillingRelatedAxiosError(error)) return true;
  if (error instanceof Error) {
    const code = (error as { code?: string }).code;
    if (code === 'ECONNABORTED' || code === 'ERR_NETWORK') return true;
  }
  return false;
}
